(function (root, factory) {
  var gallery = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = gallery;
  }

  root.ElazComboGallery = gallery;
}(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var DEFAULT_SECTION_ID = 'product-combo-media';

  function buildSectionUrl(productUrl, sectionId, origin) {
    var url = new URL(productUrl, origin);
    var expectedOrigin = new URL(origin).origin;
    if (url.origin !== expectedOrigin) {
      throw new Error('Combo media product URL must be same-origin');
    }
    url.searchParams.set('section_id', sectionId || DEFAULT_SECTION_ID);
    return url.toString();
  }

  function createGalleryLoader(options) {
    var fetchPanel = options && options.fetchPanel;
    var cache = new Map();
    var pending = new Map();

    if (typeof fetchPanel !== 'function') {
      throw new TypeError('fetchPanel must be a function');
    }

    function load(index, product) {
      if (cache.has(index)) {
        return Promise.resolve(cache.get(index));
      }
      if (pending.has(index)) {
        return pending.get(index);
      }

      var fetched;
      try {
        fetched = fetchPanel(index, product);
      } catch (error) {
        fetched = Promise.reject(error);
      }
      var request = Promise.resolve(fetched).then(function (panel) {
        cache.set(index, panel);
        pending.delete(index);
        return panel;
      }, function (error) {
        pending.delete(index);
        throw error;
      });

      pending.set(index, request);
      return request;
    }

    return {
      load: load,
      get: function (index) { return cache.get(index); },
      set: function (index, panel) { cache.set(index, panel); return panel; },
      has: function (index) { return cache.has(index); },
    };
  }

  function defaultScheduler(callback) {
    if (typeof root.requestIdleCallback === 'function') {
      root.requestIdleCallback(callback, { timeout: 2000 });
      return;
    }
    root.setTimeout(callback, 0);
  }

  function createPreloadQueue(options) {
    var load = options && options.load;
    var scheduler = options && options.scheduler || defaultScheduler;
    var saveData = Boolean(options && options.saveData);

    if (typeof load !== 'function') {
      throw new TypeError('load must be a function');
    }

    function schedule(item) {
      return new Promise(function (resolve) {
        scheduler(function () {
          Promise.resolve().then(function () {
            return load(item);
          }).catch(function () {
            return undefined;
          }).then(resolve);
        });
      });
    }

    function preload(items) {
      if (saveData) return Promise.resolve();
      return (Array.isArray(items) ? items : []).reduce(function (chain, item) {
        return chain.then(function () { return schedule(item); });
      }, Promise.resolve());
    }

    return { preload: preload };
  }

  function createGalleryController(options) {
    var loader = options.loader;
    var activate = options.activate;
    var setBusy = options.setBusy || function () {};
    var onError = options.onError || function () {};
    var token = 0;
    var busy = false;

    function show(index, product) {
      var requestToken = ++token;
      if (loader.has(index)) {
        var cachedPanel = loader.get(index);
        activate(cachedPanel, index);
        if (busy) {
          busy = false;
          setBusy(false);
        }
        return Promise.resolve(cachedPanel);
      }

      busy = true;
      setBusy(true);

      return loader.load(index, product).then(function (panel) {
        if (requestToken === token) {
          activate(panel, index);
          busy = false;
          setBusy(false);
        }
        return panel;
      }, function (error) {
        if (requestToken === token) {
          onError(error, index);
          busy = false;
          setBusy(false);
        }
        throw error;
      });
    }

    return { show: show };
  }

  function productIsAvailable(product) {
    var variants = product && Array.isArray(product.variants) ? product.variants : [];
    return variants.some(function (variant) { return Boolean(variant && variant.available); });
  }

  function preloadProductIndexes(products, initialIndex, loader) {
    return (Array.isArray(products) ? products : []).reduce(function (result, product, index) {
      if (index !== initialIndex && !loader.has(index) && productIsAvailable(product)) {
        result.push(index);
      }
      return result;
    }, []);
  }

  var BaseElement = root.HTMLElement || function () {};

  class ComboMediaGallery extends BaseElement {
    configure(products, options) {
      var gallery = this;
      var settings = options || {};
      this.products = Array.isArray(products) ? products : [];
      this.sectionId = this.dataset.comboSectionId || DEFAULT_SECTION_ID;
      this.initialIndex = Number.parseInt(this.dataset.comboInitialProductIndex, 10);
      if (!Number.isInteger(this.initialIndex)) this.initialIndex = 0;
      this.galleryDocument = settings.document || root.document;
      this.galleryFetch = settings.fetch || (typeof root.fetch === 'function' ? root.fetch.bind(root) : null);
      this.galleryOrigin = settings.origin || (root.location && root.location.origin);

      this.loader = createGalleryLoader({
        fetchPanel: function (index, product) { return gallery.fetchPanel(index, product); },
      });

      Array.prototype.forEach.call(this.querySelectorAll('[data-combo-media-panel]'), function (panel) {
        var index = Number.parseInt(panel.dataset.comboProductIndex, 10);
        if (Number.isInteger(index) && index >= 0 && index < gallery.products.length) {
          gallery.loader.set(index, panel);
        }
      });

      this.controller = createGalleryController({
        loader: this.loader,
        activate: function (panel) { gallery.activate(panel); },
        setBusy: function (busy) { gallery.setBusy(busy); },
        onError: settings.onError || function (error) {
          if (typeof gallery.dispatchEvent === 'function' && typeof root.CustomEvent === 'function') {
            gallery.dispatchEvent(new root.CustomEvent('combo-gallery-error', { detail: error }));
          }
        },
      });

      this.startPreloadAfterWindowLoad(settings);
      return this;
    }

    fetchPanel(index, product) {
      var gallery = this;
      if (typeof this.galleryFetch !== 'function') {
        return Promise.reject(new Error('Combo media gallery fetch is unavailable'));
      }
      var url = buildSectionUrl(product.url, this.sectionId, this.galleryOrigin);
      return this.galleryFetch(url, { headers: { Accept: 'text/html' } }).then(function (response) {
        if (!response.ok) throw new Error('Unable to load combo media gallery (' + response.status + ')');
        return response.text();
      }).then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var panel = parsed.querySelector('[data-combo-media-panel]');
        if (!panel) throw new Error('Combo media response is missing its panel');
        panel.dataset.comboProductIndex = String(index);
        var imported = gallery.galleryDocument.importNode(panel, true);
        imported.hidden = true;
        gallery.appendChild(imported);
        return imported;
      });
    }

    activate(target) {
      if (!target) return;
      var panels = this.querySelectorAll('[data-combo-media-panel]');
      Array.prototype.forEach.call(panels, function (panel) {
        panel.hidden = panel !== target;
      });
      target.hidden = false;
    }

    setBusy(busy) {
      if (busy) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
    }

    showProduct(index) {
      if (!this.controller) return Promise.reject(new Error('Combo media gallery is not configured'));
      if (!Number.isInteger(index) || !this.products[index]) {
        return Promise.reject(new RangeError('Unknown combo product index'));
      }
      return this.controller.show(index, this.products[index]);
    }

    startPreloadAfterWindowLoad(options) {
      if (this.preloadStarted) return;
      this.preloadStarted = true;
      var gallery = this;
      var connection = root.navigator && root.navigator.connection;
      var queue = createPreloadQueue({
        scheduler: options.scheduler,
        saveData: Boolean(connection && connection.saveData),
        load: function (index) { return gallery.loader.load(index, gallery.products[index]); },
      });
      var start = function () {
        var indexes = preloadProductIndexes(gallery.products, gallery.initialIndex, gallery.loader);
        queue.preload(indexes);
      };

      if (this.galleryDocument.readyState === 'complete') start();
      else root.addEventListener('load', start, { once: true });
    }
  }

  if (root.customElements && !root.customElements.get('combo-media-gallery')) {
    root.customElements.define('combo-media-gallery', ComboMediaGallery);
  }

  return {
    buildSectionUrl: buildSectionUrl,
    createGalleryLoader: createGalleryLoader,
    createPreloadQueue: createPreloadQueue,
    createGalleryController: createGalleryController,
    productIsAvailable: productIsAvailable,
    preloadProductIndexes: preloadProductIndexes,
    defaultScheduler: defaultScheduler,
    ComboMediaGallery: ComboMediaGallery,
  };
}));
